import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import type { SectionFieldErrors, SectionForm } from '../../shared/section-form'
import type { LockedDisplay, RenderFit } from '../../shared/types'

export interface AdvancedFieldsProps {
  form: SectionForm
  errors: SectionFieldErrors
  onChange: (patch: Partial<SectionForm>) => void
}

export default function AdvancedFields({
  form,
  errors,
  onChange,
}: AdvancedFieldsProps) {
  const { formatMessage } = useIntl()

  // A colour section paints a flat fill: it has no `_BK*` layers to place, and object-fit does
  // nothing to a solid colour. Showing either would be offering a control that cannot do anything.
  const paintsArtwork = form.kind !== 'color'

  const hint = (id: string) => (
    <Typography variant="caption" color="text.secondary">
      {formatMessage({ id })}
    </Typography>
  )

  return (
    <Accordion disableGutters={true} elevation={0} square={true}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">
          {formatMessage({ id: 'avatarStudio.editor.advanced' })}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Stack spacing={0.25}>
              <TextField
                size="small"
                type="number"
                label={formatMessage({ id: 'avatarStudio.editor.paintOrder' })}
                value={form.paintOrder}
                onChange={(e) => onChange({ paintOrder: e.target.value })}
                error={Boolean(errors.paintOrder)}
                helperText={errors.paintOrder}
                inputProps={{ min: 0, max: 1000 }}
              />
              {hint('avatarStudio.editor.paintOrderHint')}
            </Stack>
            {paintsArtwork && (
              <Stack spacing={0.25}>
                <TextField
                  size="small"
                  type="number"
                  label={formatMessage({
                    id: 'avatarStudio.editor.backPaintOrder',
                  })}
                  value={form.backPaintOrder}
                  onChange={(e) => onChange({ backPaintOrder: e.target.value })}
                  inputProps={{ min: 0, max: 1000 }}
                />
                {hint('avatarStudio.editor.backPaintOrderHint')}
              </Stack>
            )}
          </Stack>

          <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
            <Stack spacing={0.25}>
              <TextField
                select={true}
                size="small"
                sx={{ minWidth: 160 }}
                label={formatMessage({ id: 'avatarStudio.editor.lockedDisplay' })}
                value={form.lockedDisplay}
                onChange={(e) =>
                  onChange({
                    lockedDisplay: e.target.value as LockedDisplay | '',
                  })
                }
              >
                <MenuItem value="">
                  {formatMessage({
                    id: 'avatarStudio.editor.lockedDisplay.unset',
                  })}
                </MenuItem>
                <MenuItem value="teaser">
                  {formatMessage({
                    id: 'avatarStudio.editor.lockedDisplay.teaser',
                  })}
                </MenuItem>
                <MenuItem value="hidden">
                  {formatMessage({
                    id: 'avatarStudio.editor.lockedDisplay.hidden',
                  })}
                </MenuItem>
              </TextField>
              {hint('avatarStudio.editor.lockedDisplayHint')}
            </Stack>

            <TextField
              size="small"
              type="number"
              label={formatMessage({ id: 'avatarStudio.editor.renderOpacity' })}
              value={form.renderOpacity}
              onChange={(e) => onChange({ renderOpacity: e.target.value })}
              error={Boolean(errors.renderOpacity)}
              helperText={errors.renderOpacity}
              inputProps={{ min: 0.001, max: 1, step: 0.1 }}
            />

            {paintsArtwork && (
              <TextField
                select={true}
                size="small"
                sx={{ minWidth: 120 }}
                label={formatMessage({ id: 'avatarStudio.editor.renderFit' })}
                value={form.renderFit}
                onChange={(e) =>
                  onChange({ renderFit: e.target.value as RenderFit })
                }
              >
                <MenuItem value="fill">
                  {formatMessage({ id: 'avatarStudio.editor.renderFit.fill' })}
                </MenuItem>
                <MenuItem value="cover">
                  {formatMessage({ id: 'avatarStudio.editor.renderFit.cover' })}
                </MenuItem>
              </TextField>
            )}
          </Stack>

          {Object.keys(form.tints).length > 0 && (
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                {formatMessage({ id: 'avatarStudio.editor.tints' })}
              </Typography>
              {Object.entries(form.tints).map(([role, source]) => (
                <Typography key={role} variant="body2" translate="no">
                  {role} → {source}
                </Typography>
              ))}
              <Typography variant="caption" color="text.secondary">
                {formatMessage({ id: 'avatarStudio.editor.tintsReadOnly' })}
              </Typography>
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}
